import React, { useContext, useEffect, useMemo, useState } from "react";
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, TextField } from "@mui/material";
import { useQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { UserContext } from "../../auth/auth-context";
import { usePermissions } from "../../../hooks/usePermissions";
import { getEffectivePlan } from "../../../util/utils";
import UpgradePlan from "../../../util/upgrade/upgradePlan";
import { GET_BUSINESS_CATEGORIES } from "../../../graphql/queries";
import { getDisplayedBusinessSubcategories, getDisplayedBusinessTopLevelCategories } from "../../../../core/business/category";
import { ADDITIONAL_BUSINESS_REQUIRED_PLAN, hasAdditionalBusinessPlanAccess } from "../../../../core/business/businessRegistrationAccess";

export const RegisterBusinessDialog = ({ open, onClose, source = "user-profile", title = "Add New Business" }: {
  open: boolean; onClose: () => void; source?: string; title?: string;
  verticalId?: string; expectedBusinessId?: number | null;
}) => {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const { data, loading, error } = useQuery(GET_BUSINESS_CATEGORIES, { skip: !open });
  const categories = data?.getBusinessCategories || [];
  const topCategories = getDisplayedBusinessTopLevelCategories(categories, "booking");
  const subcategories = getDisplayedBusinessSubcategories(categories, categoryId, "booking");
  useEffect(() => {
    if (open) { setName(""); setCategoryId(""); setSubcategoryId(""); setConfirmed(false); }
  }, [open]);
  const continueToProfile = () => {
    const params = new URLSearchParams({ v: "booking", new: "1", categoryId });
    if (subcategoryId) params.set("subcategoryId", subcategoryId);
    navigate(`/biz/business?${params}`, { state: {
      forceNewBusiness: true, source, suggestedBusinessName: name.trim(),
      prefillCategoryId: Number(categoryId), prefillSubcategoryId: Number(subcategoryId) || null,
    } });
    onClose();
  };
  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <Stack spacing={2} sx={{ pt: 1 }}>
        {error && <Alert severity="error">Business categories could not be loaded. Please try again.</Alert>}
        <TextField label="Business name" value={name} onChange={e => setName(e.target.value)} fullWidth required />
        <TextField select label="Business category" value={categoryId} disabled={loading} onChange={e => { setCategoryId(e.target.value); setSubcategoryId(""); }} fullWidth required>
          {topCategories.map((category: any) => <MenuItem key={category.id} value={String(category.id)}>{category.name}</MenuItem>)}
        </TextField>
        {subcategories.length > 0 && <TextField select label="Business type" value={subcategoryId} onChange={e => setSubcategoryId(e.target.value)} fullWidth required>
          {subcategories.map((category: any) => <MenuItem key={category.id} value={String(category.id)}>{category.name}</MenuItem>)}
        </TextField>}
        <FormControlLabel control={<Checkbox checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />} label="I am authorized to manage bookings for this business." />
      </Stack>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="contained" onClick={continueToProfile} disabled={!name.trim() || !categoryId || (subcategories.length > 0 && !subcategoryId) || !confirmed}>Continue</Button>
    </DialogActions>
  </Dialog>;
};

const BusinessSettings = ({ buttonLabel, buttonSize = "medium" }: { buttonLabel?: string; buttonSize?: "small" | "medium" | "large"; preferredVerticalId?: string | null; source?: string } = {}) => {
  const navigate = useNavigate();
  return <Button variant="outlined" size={buttonSize} startIcon={<AddRoundedIcon />} onClick={() => navigate("/workspaces")}
    sx={{ minHeight: 44, px: 2, borderRadius: 2, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
    {buttonLabel || "Add workspace"}
  </Button>;
};
export default BusinessSettings;
